// src/pages/Roadmap.jsx
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useFitHeight } from "components/ui/compact";
import { useLocation } from "react-router-dom";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { PageHeader, StatGrid, Stat } from "components/ui/page-header";
import { Plus, Target, X, Download } from "lucide-react";
import CreateRoadmapItemDialog from "components/roadmap/CreateRoadmapItemDialog";
import EditRoadmapItemDialog from "components/roadmap/EditRoadmapItemDialog";
import JiraImportDialog from "components/roadmap/JiraImportDialog";
import { integrationsEnabled, getIntegrationStatus } from "lib/integrations";
import RoadmapBoard from "components/roadmap/RoadmapBoard";
import { roadmapItemsService, problemCardsService, okrsService } from "../services/database";
import { createInitiative, ensureInitiativeReminder } from "../services/productBrain";
import { useAuth } from "../contexts/AuthContext";

export default function Roadmap() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [problems, setProblems] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showJiraImport, setShowJiraImport] = useState(false);
  const [jiraConfig, setJiraConfig] = useState(null);

  const boardRef = useRef(null);
  const laneMaxHeight = useFitHeight(boardRef);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initiativeIdFilter = params.get("initiativeId") || "";
  const problemIdFilter = params.get("problemId") || ""; // NEW

  const [filters, setFilters] = useState({
    okr: "all",
    status: "all",
  });

  const loadData = useCallback(async () => {
    // Don't load if still authenticating or no user
    if (loading || !user?.id) {
      if (!loading && !user?.id) {
        // Auth completed but no user - clear data
        setItems([]);
        setProblems([]);
        setOkrs([]);
        setIsLoading(false);
      }
      return;
    }

    setIsLoading(true);
    try {
      console.log('Roadmap loading data for user:', user.id);

      const [ri, pc, okrData] = await Promise.all([
        roadmapItemsService.list("-updated_at"),
        problemCardsService.list("-updated_at", 999),
        okrsService.list("-updated_at", 999),
      ]);

      console.log('Roadmap data loaded:', {
        roadmapItems: ri.length,
        problems: pc.length,
        okrs: okrData.length
      });

      setItems(ri);
      setProblems(pc);
      setOkrs(okrData);
    } catch (error) {
      console.error('Error loading Roadmap data:', error);
      setItems([]);
      setProblems([]);
      setOkrs([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, loading]);

  useEffect(() => {
    loadData();
    window.addEventListener('spcc:autopilot-updated', loadData);
    return () => window.removeEventListener('spcc:autopilot-updated', loadData);
  }, [loadData]);

  // Load JIRA configuration
  useEffect(() => {
    const loadJiraConfig = async () => {
      if (!user?.id) return;
      try {
        // The Jira import is offered once the user has connected their own Jira.
        if (!integrationsEnabled) return;
        const status = await getIntegrationStatus();
        setJiraConfig(status.jira ? { connected: true } : null);
      } catch (error) {
        console.error('Failed to load JIRA config:', error);
      }
    };

    loadJiraConfig();
  }, [user?.id]);

  const problemById = useMemo(() => {
    const map = new Map();
    problems.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [problems]);

  // Apply OKR/status filters + optional problemId filter from URL
  const filtered = useMemo(() => {
    return items.filter((it) => {
      const okrMatch = filters.okr === "all" || (it.okr_ref || "") === filters.okr;
      const statusMatch = filters.status === "all" || it.status === filters.status;
      const problemMatch = !problemIdFilter || (it.problem_card_ids || []).map(String).includes(String(problemIdFilter));
      return okrMatch && statusMatch && problemMatch && (!initiativeIdFilter || it.id === initiativeIdFilter);
    });
  }, [items, filters, problemIdFilter, initiativeIdFilter]);

  const okrUniverse = useMemo(() => {
    const s = new Set();
    items.forEach((i) => { if (i.okr_ref) s.add(i.okr_ref); });
    problems.forEach((p) => { if (p.okr_ref) s.add(p.okr_ref); });
    return Array.from(s).sort();
  }, [items, problems]);

  const handleCreate = async (payload) => {
    try {
      console.log('Creating roadmap item:', payload);

      const result = await createInitiative(payload);
      await ensureInitiativeReminder(result);
      setShowCreate(false);
      await loadData();
      console.log('Roadmap item created successfully');
    } catch (error) {
      console.error('Error creating roadmap item:', error);
      console.error('Error details:', error.message, error.code, error.details);
      throw error;
    }
  };

  const handleMove = async (id, newHorizon) => {
    try {
      console.log('Moving roadmap item:', id, 'to horizon:', newHorizon);
      await roadmapItemsService.update(id, { time_horizon: newHorizon });
      loadData();
    } catch (error) {
      console.error('Error moving roadmap item:', error);
      alert('Failed to move roadmap item. Please try again.');
    }
  };

  const handleUpdate = async (id, patch) => {
    try {
      console.log('Updating roadmap item:', id, 'with:', patch);
      await roadmapItemsService.update(id, patch);
      loadData();
    } catch (error) {
      console.error('Error updating roadmap item:', error);
      alert('Failed to update roadmap item. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowEdit(true);
  };

  const handleEditSubmit = async (id, payload) => {
    try {
      console.log('Editing roadmap item:', id, payload);
      const updated = await roadmapItemsService.update(id, payload);
      await ensureInitiativeReminder(updated);
      setShowEdit(false);
      setEditingItem(null);
      await loadData();
      console.log('Roadmap item updated successfully');
    } catch (error) {
      console.error('Error updating roadmap item:', error);
      throw error;
    }
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setEditingItem(null);
  };

  const handleJiraImport = async (initiatives) => {
    try {
      console.log('🚀 Importing', initiatives.length, 'initiatives from JIRA');

      for (const initiative of initiatives) {
        // Map JIRA initiative to existing database schema
        const dbPayload = {
          title: initiative.title,
          description: `${initiative.description || ''}${initiative.description ? '\n\n' : ''}[Imported from JIRA: ${initiative.jira_key}]`,
          time_horizon: initiative.time_horizon,
          status: initiative.status,
          owner: initiative.owner || null,
          tags: Array.isArray(initiative.tags) ? [...initiative.tags, 'jira-import', initiative.jira_type?.toLowerCase()] : ['jira-import'],
          expected_impact: initiative.expected_impact,

          // Use existing fields to store JIRA metadata
          problem_statement: `JIRA ${initiative.jira_type}: ${initiative.jira_key}`,
          solution_approach: initiative.jira_components ? `Components: ${initiative.jira_components.join(', ')}` : null,
          effort_estimate: initiative.jira_effort ? `${initiative.jira_effort} story points` : null,

          // Set priority and learning scores based on JIRA data
          priority_score: Number(initiative.expected_impact) || 3,
          learning_score: 0,

          // Use created/updated dates from JIRA
          created_at: initiative.created_date,
          updated_at: initiative.updated_date
        };

        console.log('Creating roadmap item from JIRA:', dbPayload);
        await roadmapItemsService.create(dbPayload);
      }

      await loadData();
      console.log('✅ Successfully imported', initiatives.length, 'initiatives');
    } catch (error) {
      console.error('❌ JIRA import failed:', error);
      throw error;
    }
  };

  const clearProblemFilter = () => {
    const sp = new URLSearchParams(location.search);
    sp.delete("problemId");
    const next = `${location.pathname}${sp.toString() ? `?${sp.toString()}` : ""}`;
    window.history.replaceState({}, "", next);
    // No need to reload; `problemIdFilter` will be empty on next render
  };

  return (
    <div>
      <PageHeader
        icon={Target}
        eyebrow="Plan"
        title="Outcome Roadmap"
        description="Grouped by horizon. Connect problems, outcomes, and objectives."
        actions={<>
          <select
            aria-label="OKR filter"
            value={filters.okr}
            onChange={(e) => setFilters((f) => ({ ...f, okr: e.target.value }))}
            style={{ width: 180 }}
          >
            <option value="all">All OKRs</option>
            {okrUniverse.map((o) => (
              <option key={o} value={o}>{okrs.find(okr => okr.id === o)?.title || "Unresolved objective"}</option>
            ))}
          </select>
          <select
            aria-label="Status filter"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            style={{ width: 150 }}
          >
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
          </select>
          {jiraConfig && (
            <Button onClick={() => setShowJiraImport(true)} variant="secondary">
              <Download size={15} />
              Import from Jira
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} variant="primary">
            <Plus size={15} />
            New initiative
          </Button>
        </>}
      />

      {problemIdFilter && (
        <div style={{ marginBottom: 12 }}>
          <Badge variant="accent">
            Problem: {problemById.get(String(problemIdFilter))?.title || problemIdFilter}
            <button
              type="button"
              onClick={clearProblemFilter}
              aria-label="Clear problem filter"
              style={{ border: 0, background: "transparent", padding: 0, display: "inline-flex", color: "inherit" }}
            >
              <X size={12} />
            </button>
          </Badge>
        </div>
      )}

      <StatGrid>
        <Stat label="Total initiatives" value={items.length} />
        <Stat label="Linked to problems" value={items.filter((i) => i.problem_id || (i.problem_card_ids || []).length).length} />
        <Stat label="In progress" value={items.filter((i) => i.status === "in_progress").length} />
        <Stat label="Done" value={items.filter((i) => i.status === "done").length} />
      </StatGrid>
      <WorkspaceInsights area="roadmap"/>

      {/* Board: on wide screens each horizon scrolls on its own, ending at the window bottom */}
      <div ref={boardRef}>
        <RoadmapBoard
          items={filtered}
          problems={problems}
          okrs={okrs}
          isLoading={isLoading}
          onMove={handleMove}
          onUpdate={handleUpdate}
          onEdit={handleEdit}
          laneMaxHeight={laneMaxHeight}
        />
      </div>

      {/* Create dialog */}
      <CreateRoadmapItemDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        problems={problems}
      />

      {/* Edit dialog */}
      <EditRoadmapItemDialog
        open={showEdit}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        problems={problems}
        item={editingItem}
      />

      {/* JIRA Import dialog */}
      {jiraConfig && (
        <JiraImportDialog
          open={showJiraImport}
          onClose={() => setShowJiraImport(false)}
          onImport={handleJiraImport}
          jiraConfig={jiraConfig}
        />
      )}
    </div>
  );
}
